import React, { useEffect, useState } from "react";
import Tab from "./Tab";
import BEHotelDetails from "./BEHotelDetails/BEHotelDetails";
import BEPaymentSettings from "./BEPaymentSettings/Index";
import SocialLinks from "./SocialLinks/SocialLinks";
import NearByAttraction from "./NearBySection/Index";
import { useDispatch, useSelector } from "react-redux";
import {
  postHotelContentDetails,
  updateHotelContentDetails,
  updateHotelDetailsAsync,
} from "../../../reducer/loginReducer";
import { message } from "antd";
import { CircularProgress } from "@mui/material";
import { TranslateText } from "../../../utils/translation";
import DiscountSettings from "./discount-settings";
import { getBookingEngineHotelInfoBySubdomain } from "../../../services/hotel";
import { BiLinkExternal } from "react-icons/bi";

const Index = ({ fullPage = false }) => {
  const [title, setTitle] = useState("Hotel Details");
  const { editHotelDetails } = useSelector((store) => store.usersRestriction);
  const { menuDrawer } = useSelector((store) => store.app);
  const [loading, setLoading] = useState(false);
  const [subdomainSuggestions, setSubdomainSuggestions] = useState([]);
  const [subdomainAvailabilityMessage, setSubdomainAvailabilityMessage] =
    useState("");

  const dispatch = useDispatch();

  const handleNext = () => {
    title === "Hotel Details"
      ? setTitle("Nearby Attraction")
      : title === "Nearby Attraction"
        ? setTitle("Payment settings")
        : title === "Payment settings"
          ? setTitle("Social Links")
          : setTitle("Social Links");
  };

  const { hotelDetails, hotelContentDetails } = useSelector(
    (store) => store.login,
  );

  const [localHotelDetailsState, setLocalHotelDetailsState] = useState({});

  useEffect(() => {
    setLocalHotelDetailsState({ ...hotelDetails, ...hotelContentDetails });
  }, [hotelContentDetails, hotelDetails]);

  const normalizeSubdomain = (value = "") => {
    return value
      ?.toString()
      ?.trim()
      ?.toLowerCase()
      ?.replace(/\s+/g, "-")
      ?.replace(/[^a-z0-9-]/g, "")
      ?.replace(/-+/g, "-")
      ?.replace(/^-+|-+$/g, "");
  };

  const isSubdomainFormatValid = (subdomain = "") => {
    const normalizedSubdomain = normalizeSubdomain(subdomain);
    if (!normalizedSubdomain) return true;

    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalizedSubdomain);
  };

  const checkSubdomainAvailability = async (
    subdomain = "",
    { showError = true } = {},
  ) => {
    const normalizedSubdomain = normalizeSubdomain(subdomain);

    if (!normalizedSubdomain) return { available: true };

    try {
      const response =
        await getBookingEngineHotelInfoBySubdomain(normalizedSubdomain);
      const mappedHotelId = response?.data?.hotelId;

      // Same hotel updating its own subdomain is allowed.
      if (!mappedHotelId || mappedHotelId === hotelDetails?.id) {
        return { available: true };
      }

      return { available: false };
    } catch (error) {
      // If not found, subdomain is available.
      if (error?.response?.status === 404) {
        return { available: true };
      }

      if (showError) {
        message.error("Unable to verify subdomain right now. Please retry.");
      }
      return { available: false, error: true };
    }
  };

  const generateSubdomainCandidates = (subdomain = "") => {
    const normalizedSubdomain = normalizeSubdomain(subdomain);
    const citySlug = normalizeSubdomain(localHotelDetailsState?.city || "");
    const base = normalizedSubdomain || "hotel";
    const currentYear = new Date().getFullYear();

    return [
      `${base}-hotel`,
      `${base}-stay`,
      `${base}-inn`,
      `${base}-${citySlug}`,
      `${base}-${currentYear}`,
      `${base}-${Math.floor(Math.random() * 900 + 100)}`,
      `${base}-official`,
      `${base}-booking`,
    ]
      .map((value) => normalizeSubdomain(value))
      .filter(Boolean)
      .filter((value, index, arr) => arr.indexOf(value) === index)
      .filter((value) => value !== normalizedSubdomain);
  };

  const getAvailableSubdomainSuggestions = async (subdomain = "") => {
    const candidates = generateSubdomainCandidates(subdomain);
    const availableSuggestions = [];

    for (const candidate of candidates) {
      const response = await checkSubdomainAvailability(candidate, {
        showError: false,
      });

      if (response?.available) {
        availableSuggestions.push(candidate);
      }

      if (availableSuggestions.length >= 4) {
        break;
      }
    }

    return availableSuggestions;
  };

  const getDefaultSubdomainFromHotelName = async (hotelName = "") => {
    const normalizedHotelNameSubdomain = normalizeSubdomain(hotelName);
    const candidates = [
      normalizedHotelNameSubdomain,
      ...generateSubdomainCandidates(normalizedHotelNameSubdomain),
    ].filter(Boolean);

    for (const candidate of candidates) {
      const response = await checkSubdomainAvailability(candidate, {
        showError: false,
      });
      if (response?.available) {
        return candidate;
      }
    }

    return normalizedHotelNameSubdomain || "";
  };

  useEffect(() => {
    const maybePrefillSubdomain = async () => {
      const persistedSubdomain = normalizeSubdomain(
        hotelContentDetails?.bookingEngineSubdomain,
      );

      // If subdomain is already set in DB, do not run any suggestion logic.
      if (persistedSubdomain) {
        setSubdomainSuggestions([]);
        setSubdomainAvailabilityMessage("");
        return;
      }

      const currentSubdomain = normalizeSubdomain(
        localHotelDetailsState?.bookingEngineSubdomain,
      );

      // Only suggest/prefill if never chosen.
      if (currentSubdomain) return;

      const hotelNameForSuggestion =
        localHotelDetailsState?.name || hotelDetails?.name;
      if (!hotelNameForSuggestion) return;

      const suggestedSubdomain = await getDefaultSubdomainFromHotelName(
        hotelNameForSuggestion,
      );

      if (!suggestedSubdomain) return;

      setLocalHotelDetailsState((prev) => {
        const prevSubdomain = normalizeSubdomain(prev?.bookingEngineSubdomain);
        if (prevSubdomain) return prev;

        return {
          ...prev,
          bookingEngineSubdomain: suggestedSubdomain,
        };
      });

      const backupSuggestions =
        await getAvailableSubdomainSuggestions(suggestedSubdomain);
      setSubdomainSuggestions(
        [suggestedSubdomain, ...backupSuggestions]
          .filter(Boolean)
          .filter((value, index, arr) => arr.indexOf(value) === index)
          .slice(0, 4),
      );
      setSubdomainAvailabilityMessage(
        "Suggested based on hotel name. You can edit it before saving.",
      );
    };

    maybePrefillSubdomain();
  }, [
    localHotelDetailsState?.name,
    hotelDetails?.name,
    hotelContentDetails?.bookingEngineSubdomain,
  ]);

  const handleSave = async () => {
    setLoading(true);
    // First we need to build payload depending on which page we are

    if (title === "Hotel Details") {
      if (!isHotelDetailsValid()) {
        setLoading(false);
        return;
      }

      const normalizedSubdomain = normalizeSubdomain(
        localHotelDetailsState?.bookingEngineSubdomain,
      );

      setSubdomainSuggestions([]);
      setSubdomainAvailabilityMessage("");

      if (!isSubdomainFormatValid(normalizedSubdomain)) {
        message.info(
          "Subdomain can only contain lowercase letters, numbers and hyphens",
        );
        setLoading(false);
        return;
      }

      const availabilityResponse =
        await checkSubdomainAvailability(normalizedSubdomain);
      if (!availabilityResponse?.available) {
        const suggestions =
          await getAvailableSubdomainSuggestions(normalizedSubdomain);

        setSubdomainSuggestions(suggestions);
        setSubdomainAvailabilityMessage(
          suggestions.length > 0
            ? "This subdomain is already in use. Try one of these available options:"
            : "This subdomain is already in use. Please try a different one.",
        );

        message.info("This subdomain is already in use. Please choose another");
        setLoading(false);
        return;
      }

      const hotelDetailsPayload = {
        hotelId: hotelDetails?.id,
        body: {
          ...hotelDetails,
          name: localHotelDetailsState?.name,
          address: localHotelDetailsState?.address,
          contactNumber: localHotelDetailsState?.contactNumber,
          contactNumbers: localHotelDetailsState?.contactNumbers,
          emailAddress: localHotelDetailsState?.emailAddress,
          logo: localHotelDetailsState?.logo,
          images: localHotelDetailsState?.images,
        },
      };

      const hotelDetailsResponse = await dispatch(
        updateHotelDetailsAsync(hotelDetailsPayload),
      );

      if (Boolean(hotelContentDetails) && Boolean(hotelContentDetails?.id)) {
        const hotelContentPayload = {
          id: hotelContentDetails?.id,
          body: {
            ...hotelContentDetails,
            coverImage: localHotelDetailsState?.coverImage,
            aboutUs: localHotelDetailsState?.aboutUs,
            googleMapsLink: localHotelDetailsState?.googleMapsLink,
            bookingEngineSubdomain: normalizedSubdomain || null,
          },
        };

        const response = await dispatch(
          updateHotelContentDetails(hotelContentPayload),
        );
        if (!response?.payload?.id) message.info("Something went wrong");
      } else {
        const payload = {
          hotelId: hotelDetails?.id,
          coverImage: localHotelDetailsState?.coverImage,
          aboutUs: localHotelDetailsState?.aboutUs,
          googleMapsLink: localHotelDetailsState?.googleMapsLink,
          bookingEngineSubdomain: normalizedSubdomain || null,
        };

        const response = await dispatch(postHotelContentDetails(payload));
        if (!response?.payload?.id) message.info("Something went wrong");
      }
    }

    if (title === "Nearby Attraction") {
      if (Boolean(hotelContentDetails) && Boolean(hotelContentDetails?.id)) {
        const hotelContentPayload = {
          id: hotelContentDetails?.id,
          body: {
            ...hotelContentDetails,
            nearbyAttractions: Array.isArray(
              localHotelDetailsState?.nearbyAttractions,
            )
              ? localHotelDetailsState?.nearbyAttractions
              : null,
          },
        };

        const response = await dispatch(
          updateHotelContentDetails(hotelContentPayload),
        );
        if (response?.payload?.id) message.success("Data Successfully Updated");
        else message.info("Something went wrong");
      } else {
        const payload = {
          hotelId: hotelDetails?.id,
          nearbyAttractions: Array.isArray(
            localHotelDetailsState?.nearbyAttractions,
          )
            ? localHotelDetailsState?.nearbyAttractions
            : null,
        };

        const response = await dispatch(postHotelContentDetails(payload));

        if (response?.payload?.id) message.success("Data Successfully Updated");
        else message.info("Something went wrong");
      }
    }

    if (title === "Payment settings" || title === "Price Settings") {
      const hotelDetailsPayload = {
        hotelId: hotelDetails?.id,
        body: {
          ...hotelDetails,
          currency: localHotelDetailsState?.currency,
          taxIncludedInPrice: Boolean(
            localHotelDetailsState?.taxIncludedInPrice,
          ),
        },
      };

      const hotelDetailsResponse = await dispatch(
        updateHotelDetailsAsync(hotelDetailsPayload),
      );

      if (Boolean(hotelContentDetails) && Boolean(hotelContentDetails?.id)) {
        const hotelContentPayload = {
          id: hotelContentDetails?.id,
          body: {
            ...hotelContentDetails,
            partialPaymentAllowed: Boolean(
              localHotelDetailsState?.partialPaymentAllowed,
            ),
            minBookingAmount: {
              title: Boolean(localHotelDetailsState?.minBookingAmount?.title)
                ? localHotelDetailsState?.minBookingAmount?.title
                : "percentage",
              value: localHotelDetailsState?.minBookingAmount?.value,
            },
            nonRefundablePartialPaymentAllowed:
              localHotelDetailsState?.nonRefundablePartialPaymentAllowed,

            remainingAmountToBeCollectedBeforeCheckIn: Boolean(
              localHotelDetailsState?.remainingAmountToBeCollectedBeforeCheckIn,
            ),
            noOfDaysBeforeCheckIn: Boolean(
              localHotelDetailsState?.noOfDaysBeforeCheckIn,
            )
              ? localHotelDetailsState?.noOfDaysBeforeCheckIn
              : 0,

            disablePaymentGateway:
              localHotelDetailsState?.disablePaymentGateway || false,

            priceAdjustment: localHotelDetailsState?.priceAdjustment,
          },
        };

        const response = await dispatch(
          updateHotelContentDetails(hotelContentPayload),
        );
        if (!response?.payload?.id) message.info("Something went wrong");
      } else {
        const payload = {
          hotelId: hotelDetails?.id,
          partialPaymentAllowed: Boolean(
            localHotelDetailsState?.partialPaymentAllowed,
          ),
          minBookingAmount: {
            title: Boolean(localHotelDetailsState?.minBookingAmount?.title)
              ? localHotelDetailsState?.minBookingAmount?.title
              : "percentage",
            value: localHotelDetailsState?.minBookingAmount?.value,
          },
          nonRefundablePartialPaymentAllowed:
            localHotelDetailsState?.nonRefundablePartialPaymentAllowed,
          remainingAmountToBeCollectedBeforeCheckIn: Boolean(
            localHotelDetailsState?.remainingAmountToBeCollectedBeforeCheckIn,
          ),
        };

        const response = await dispatch(postHotelContentDetails(payload));
        if (!response?.payload?.id) message.info("Something went wrong");
      }
    }

    if (title === "Social Links") {
      if (Boolean(hotelContentDetails) && Boolean(hotelContentDetails?.id)) {
        const hotelContentPayload = {
          id: hotelContentDetails?.id,
          body: {
            ...hotelContentDetails,
            socialMediaLinks: localHotelDetailsState?.socialMediaLinks,
          },
        };

        const response = await dispatch(
          updateHotelContentDetails(hotelContentPayload),
        );

        if (response?.payload?.id) message.success("Data Successfully Updated");
        else message.info("Something went wrong");
      } else {
        const payload = {
          hotelId: hotelDetails?.id,
          socialMediaLinks: localHotelDetailsState?.socialMediaLinks,
        };

        const response = await dispatch(postHotelContentDetails(payload));

        if (response?.payload?.id) message.success("Data Successfully Updated");
        else message.info("Something went wrong");
      }
    }

    setLoading(false);
  };

  const isHotelDetailsValid = () => {
    if (!localHotelDetailsState?.name) {
      message.info("Please enter hotel name");
      return false;
    }
    if (!localHotelDetailsState?.address) {
      message.info("Please enter hotel address");
      return false;
    }
    if (!localHotelDetailsState?.contactNumber) {
      message.info("Please enter Contact Number");
      return false;
    }
    if (!localHotelDetailsState?.emailAddress) {
      message.info("Please enter hotel email-Address");
      return false;
    }

    if (!localHotelDetailsState?.name) {
      message.info("Please enter hotel name");
      return false;
    }

    return true;
  };

  return (
    <div>
      {fullPage && (
        <div
          className={`fixed top-[3.625rem] h-[3.5rem] z-[30] right-0 ${
            menuDrawer ? "left-[5rem]" : "left-[13rem]"
          } flex justify-between items-center px-[1.5rem] shadow-md bg-surface`}
        >
          <div className="font-outfit font-[600] text-[22px] leading-[33px] text-[#545454]">
            <TranslateText textKey={"booking_engine"} namespace="menu" />
          </div>
          <div
            className="flex items-center gap-2 text-primary cursor-pointer font-body text-[14px] font-[500]"
            onClick={() =>
              window.open(
                `https://${hotelContentDetails?.bookingEngineSubdomain}.sites.revivotech.in`,
                "_newtab"
              )
            }
          >
            <BiLinkExternal className="text-[18px]" />
            <TranslateText textKey={"open_booking_engine"} namespace="menu" />
          </div>
        </div>
      )}

      <Tab title={title} setTitle={setTitle} fullPage={fullPage} />

      <div className={`${fullPage ? "mt-[10.75rem] px-[1.5rem]" : "mt-[4rem]"}`}>
        {title === "Hotel Details" && (
          <BEHotelDetails
            localHotelDetailsState={localHotelDetailsState}
            setLocalHotelDetailsState={setLocalHotelDetailsState}
            subdomainSuggestions={subdomainSuggestions}
            subdomainAvailabilityMessage={subdomainAvailabilityMessage}
            onSubdomainSuggestionSelect={(selectedSubdomain) =>
              setLocalHotelDetailsState({
                ...localHotelDetailsState,
                bookingEngineSubdomain: selectedSubdomain,
              })
            }
            onSubdomainInputChange={() => {
              setSubdomainSuggestions([]);
              setSubdomainAvailabilityMessage("");
            }}
          />
        )}

        {title === "Nearby Attraction" && (
          <NearByAttraction
            localHotelDetailsState={localHotelDetailsState}
            setLocalHotelDetailsState={setLocalHotelDetailsState}
          />
        )}

        {title === "Social Links" && (
          <SocialLinks
            localHotelDetailsState={localHotelDetailsState}
            setLocalHotelDetailsState={setLocalHotelDetailsState}
          />
        )}

        {title === "Payment settings" && (
          <BEPaymentSettings
            localHotelDetailsState={localHotelDetailsState}
            setLocalHotelDetailsState={setLocalHotelDetailsState}
          />
        )}

        {title === "Price Settings" && (
          <DiscountSettings
            localHotelDetailsState={localHotelDetailsState}
            setLocalHotelDetailsState={setLocalHotelDetailsState}
          />
        )}
      </div>

      {editHotelDetails && (
        <footer
          className={`shadow-mobile-footer h-[80px] fixed bottom-0 right-0 pr-[24px] flex justify-between items-center bg-surface z-[20] ${
            fullPage ? (menuDrawer ? "left-[5rem]" : "left-[13rem]") : "w-[67%]"
          }`}
        >
          <div />

          <div className="flex items-center gap-[12px]">
            <div
              className="bg-primary py-[12px] w-[190px] flex justify-center items-center font-body font-[500] text-[16px] leading-[24px] text-fg-inverse rounded-[8px] cursor-pointer"
              onClick={loading ? undefined : () => handleSave()}
            >
              {loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <TranslateText textKey={"save"} namespace="profile" />
              )}
            </div>
            <div
              className=" border-[1px] border-primary py-[12px] w-[190px] flex justify-center items-center font-body font-[500] text-[16px] leading-[24px] text-primary rounded-[8px] cursor-pointer"
              onClick={() => handleNext()}
            >
              <TranslateText textKey={"next"} namespace="profile" />
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default Index;
